<template>
  <div class="settings-block">
    <div v-show="localExpanded" class="section-content">
      <!-- Alert Banner -->
      <AlertBanner
        v-if="alert"
        :type="alert.type"
        :title="alert.title"
        :message="alert.message"
        @dismiss="emit('dismiss-alert')"
      />

      <!-- Role Information Block -->
      <div class="info-block" style="margin-bottom: 16px">
        <div class="info-block-header" @click="toggleRoleInfo">
          <span class="info-icon"><InfoIcon /></span>
          <span class="info-title">{{ s__('ProjectSettings|About roles') }}</span>
          <span class="info-toggle-icon">{{ showRoleInfo ? '▼' : '▶' }}</span>
        </div>
        <div v-show="showRoleInfo" class="info-block-content">
          <div class="role-info-item">
            <strong>{{ s__('ProjectSettings|Owner') }}</strong>
            <p>
              {{
                s__(
                  'ProjectSettings|Full control: can manage members, archive the project, and perform all administrative tasks.'
                )
              }}
            </p>
          </div>
          <div class="role-info-item">
            <strong>{{ s__('ProjectSettings|Admin') }}</strong>
            <p>
              {{
                s__(
                  'ProjectSettings|Can manage project settings, workflows, labels, and other configurations. Cannot manage members or archive the project.'
                )
              }}
            </p>
          </div>
          <div class="role-info-item">
            <strong>{{ s__('ProjectSettings|Member') }}</strong>
            <p>
              {{
                s__(
                  'ProjectSettings|Can view the project and create tasks. Limited to basic project operations.'
                )
              }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="project?.telegramChatId" class="info-block" style="margin-bottom: 16px">
        <div class="info-block-header" @click="toggleTelegramStatusInfo">
          <span class="info-icon"><InfoIcon /></span>
          <span class="info-title">{{ s__('ProjectSettings|Telegram member status') }}</span>
          <span class="info-toggle-icon">{{ localTelegramStatusExpanded ? '▼' : '▶' }}</span>
        </div>
        <div v-show="localTelegramStatusExpanded" class="info-block-content">
          <div v-if="telegramStatusLoading" class="muted">
            {{ __('Loading…') }}
          </div>
          <p v-else-if="isTelegramStatusUnavailable" class="muted" style="margin: 0">
            {{
              s__('ProjectSettings|Telegram status is temporarily unavailable. Please try later.')
            }}
          </p>
          <p v-else class="muted" style="margin: 0">
            {{
              s__(
                'ProjectSettings|Telegram checks are loaded on demand and shown directly in each member row.'
              )
            }}
          </p>
        </div>
      </div>

      <div v-if="loading" class="muted" style="margin-bottom: 12px">
        {{ __('Loading…') }}
      </div>
      <div v-else-if="sortedMemberships.length === 0" class="muted" style="margin-bottom: 12px">
        {{ s__('ProjectSettings|No members yet.') }}
      </div>
      <ul v-else style="list-style: none; padding: 0; margin: 0">
        <li v-for="membership in sortedMemberships" :key="membership.id" class="member-item">
          <div style="flex: 1">
            <div>
              <strong>{{
                membership.user.name || membership.user.username || `User ${membership.user.id}`
              }}</strong>
            </div>
            <div class="muted" style="font-size: 13px; margin-top: 2px">
              @{{ membership.user.username || 'no-username' }}
            </div>
            <div
              v-if="membership.projectRole"
              class="muted"
              style="font-size: 13px; margin-top: 2px; font-style: italic"
            >
              {{ membership.projectRole }}
            </div>
            <div
              v-if="
                localTelegramStatusExpanded &&
                telegramStatusLoaded &&
                hasTelegramWarnings(membership)
              "
              style="margin-top: 8px; display: flex; flex-direction: column; gap: 4px"
            >
              <div
                v-if="project?.telegramChatId && membership.inTelegramGroup === false"
                class="member-warning"
                style="
                  padding: 6px 10px;
                  background: #fff3cd;
                  border: 1px solid #ffc107;
                  border-radius: 4px;
                  font-size: 12px;
                  color: #856404;
                "
              >
                ⚠️ {{ s__('ProjectSettings|Member has left the Telegram group') }}
              </div>
              <div
                v-if="membership.hasDmAccess === false"
                class="member-warning"
                style="
                  padding: 6px 10px;
                  background: #d1ecf1;
                  border: 1px solid #0c5460;
                  border-radius: 4px;
                  font-size: 12px;
                  color: #0c5460;
                "
              >
                ℹ️
                {{
                  s__(
                    'ProjectSettings|Bot not added to Direct Messages or DM notifications disabled'
                  )
                }}
              </div>
            </div>
            <div
              v-if="membership.role !== 'owner'"
              style="margin-top: 8px; display: flex; flex-direction: column; gap: 8px"
            >
              <div style="display: flex; align-items: center; gap: 8px">
                <select
                  :value="membership.role"
                  class="role-select"
                  @change="updateMemberRole(membership, $event.target.value)"
                >
                  <option value="admin">{{ s__('ProjectSettings|Admin') }}</option>
                  <option value="member">{{ s__('ProjectSettings|Member') }}</option>
                </select>
                <button
                  type="button"
                  class="btn-sm btn-danger member-remove-btn"
                  style="margin-left: auto"
                  @click="removeMember(membership)"
                >
                  <span class="btn-text">{{ s__('ProjectSettings|Remove') }}</span>
                  <span><TrashIcon /></span>
                </button>
              </div>
              <div style="display: flex; align-items: center; gap: 8px">
                <input
                  :value="
                    editingProjectRole[membership.id] !== undefined
                      ? editingProjectRole[membership.id]
                      : membership.projectRole || ''
                  "
                  type="text"
                  :placeholder="
                    s__('ProjectSettings|Project role (e.g., Senior backend developer)')
                  "
                  class="form-control"
                  style="flex: 1; max-width: 400px"
                  maxlength="128"
                  @input="editingProjectRole[membership.id] = $event.target.value"
                />
                <button
                  v-if="hasProjectRoleChanged(membership)"
                  type="button"
                  class="btn-sm project-role-save-btn"
                  @click="updateProjectRole(membership, editingProjectRole[membership.id])"
                >
                  <span class="btn-text">{{ s__('ProjectSettings|Save') }}</span>
                  <span class="btn-mobile-icon"><PlusIcon /></span>
                </button>
              </div>
            </div>
            <span
              v-else
              class="role-badge"
              :class="`role-${membership.role}`"
              style="margin-top: 8px; display: inline-block"
            >
              {{ membership.role }}
            </span>
            <div
              v-if="membership.role === 'owner'"
              style="margin-top: 8px; display: flex; align-items: center; gap: 8px"
            >
              <input
                :value="
                  editingProjectRole[membership.id] !== undefined
                    ? editingProjectRole[membership.id]
                    : membership.projectRole || ''
                "
                type="text"
                :placeholder="s__('ProjectSettings|Project role (e.g., Senior backend developer)')"
                class="form-control"
                style="flex: 1; max-width: 300px"
                maxlength="128"
                @input="editingProjectRole[membership.id] = $event.target.value"
              />
              <button
                v-if="hasProjectRoleChanged(membership)"
                type="button"
                class="btn-sm project-role-save-btn"
                @click="updateProjectRole(membership, editingProjectRole[membership.id])"
              >
                <span class="btn-text">{{ s__('ProjectSettings|Save') }}</span>
                <span class="btn-mobile-icon"><PlusIcon /></span>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf } from '@/locale'
import { showConfirm } from '@/utils/telegramWebApp'
import TrashIcon from '../common/icons/TrashIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import PlusIcon from '../common/icons/PlusIcon.vue'
import AlertBanner from '../common/AlertBanner.vue'

const showRoleInfo = ref(false)

const props = defineProps({
  projectId: { type: String, required: true },
  project: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  memberships: { type: Array, default: () => [] },
  alert: { type: Object, default: null },
  onRefetch: { type: Function, default: null },
  expanded: { type: Boolean, default: false },
  telegramStatusExpanded: { type: Boolean, default: false },
  telegramStatusLoading: { type: Boolean, default: false },
  telegramStatusLoaded: { type: Boolean, default: false },
  telegramStatusLoadState: { type: String, default: 'idle' },
})

const emit = defineEmits([
  'refetch',
  'update:expanded',
  'update:telegram-status-expanded',
  'load-telegram-status',
  'show-alert',
  'dismiss-alert',
])

const localExpanded = computed({
  get: () => props.expanded,
  set: value => emit('update:expanded', value),
})

const localTelegramStatusExpanded = computed({
  get: () => props.telegramStatusExpanded,
  set: value => emit('update:telegram-status-expanded', value),
})

const isTelegramStatusUnavailable = computed(
  () => props.telegramStatusLoadState === 'timeout' || props.telegramStatusLoadState === 'error'
)

// Sort memberships: Owners first, then Admins, then Members
// Within each role, sort by name in ascending order
const sortedMemberships = computed(() => {
  if (!props.memberships || props.memberships.length === 0) {
    return []
  }

  const rolePriority = { owner: 1, admin: 2, member: 3 }

  return [...props.memberships].sort((a, b) => {
    const priorityA = rolePriority[a.role] || 99
    const priorityB = rolePriority[b.role] || 99

    // First sort by role priority
    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }

    // Within the same role, sort alphabetically by name (ascending)
    // Use name if available, otherwise fall back to username
    const getName = user => {
      if (user?.name) return user.name.trim()
      if (user?.username) return user.username.trim()
      return ''
    }

    const nameA = getName(a.user).toLowerCase()
    const nameB = getName(b.user).toLowerCase()

    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
  })
})

const editingProjectRole = ref({})

const UPDATE_MEMBER_ROLE = gql`
  mutation UpdateMemberRole($membershipId: ID!, $role: String!, $projectRole: String) {
    updateMemberRole(membershipId: $membershipId, role: $role, projectRole: $projectRole) {
      membership {
        id
        role
        projectRole
      }
      errors
    }
  }
`

const REMOVE_MEMBER = gql`
  mutation RemoveMember($membershipId: ID!, $reassignToUserId: ID) {
    removeMember(membershipId: $membershipId, reassignToUserId: $reassignToUserId) {
      membership {
        id
      }
      reassignedTasksCount
      userStillInGroup
      errors
    }
  }
`

const { mutate: updateMemberRoleMutation } = useMutation(UPDATE_MEMBER_ROLE)
const { mutate: removeMemberMutation } = useMutation(REMOVE_MEMBER)

function toggleRoleInfo() {
  showRoleInfo.value = !showRoleInfo.value
}

function hasTelegramWarnings(membership) {
  return (
    (props.project?.telegramChatId && membership.inTelegramGroup === false) ||
    membership.hasDmAccess === false
  )
}

function toggleTelegramStatusInfo() {
  const nextExpanded = !localTelegramStatusExpanded.value
  localTelegramStatusExpanded.value = nextExpanded

  if (nextExpanded && !props.telegramStatusLoading && props.telegramStatusLoadState !== 'success') {
    emit('load-telegram-status')
  }
}

function hasProjectRoleChanged(membership) {
  const currentValue = editingProjectRole.value[membership.id]
  if (currentValue === undefined) return false
  const originalValue = membership.projectRole || ''
  return currentValue.trim() !== originalValue.trim()
}

async function updateMemberRole(membership, newRole) {
  try {
    // Use edited projectRole if available, otherwise use current value
    let projectRoleValue
    if (editingProjectRole.value[membership.id] !== undefined) {
      // User has edited the field - send empty string to clear, or the trimmed value
      const trimmedValue = (editingProjectRole.value[membership.id] || '').trim()
      projectRoleValue = trimmedValue === '' ? '' : trimmedValue
    } else {
      // No edits - use current value (send "" if empty, or the value)
      projectRoleValue = membership.projectRole || ''
    }

    const { data } = await updateMemberRoleMutation({
      membershipId: membership.id,
      role: newRole,
      projectRole: projectRoleValue, // Always send a value ("" to clear, or the string)
    })

    if (data?.updateMemberRole?.errors?.length) {
      throw new Error(data.updateMemberRole.errors.join(', '))
    }

    // Clear any edited projectRole since we just saved it
    delete editingProjectRole.value[membership.id]

    // Emit success alert BEFORE refetch (so parent receives it before component re-renders)
    emit('show-alert', {
      type: 'success',
      title: s__('ProjectSettings|Success'),
      message: s__('ProjectSettings|Member role updated successfully'),
    })

    // Refetch to get updated data from server
    await refetch()
  } catch (e) {
    console.error('[MembersList] Error in updateMemberRole', e)

    // Emit error alert BEFORE refetch
    emit('show-alert', {
      type: 'error',
      title: s__('ProjectSettings|Error'),
      message: sprintf(s__('ProjectSettings|Failed to update role: %{error}'), {
        error: e.message || s__('ProjectSettings|Unknown error'),
      }),
    })

    await refetch() // Revert UI
  }
}

async function updateProjectRole(membership, newProjectRole) {
  // Convert to trimmed value - empty string means clear, otherwise use the value
  const trimmedValue = (newProjectRole || '').trim()
  const projectRoleValue = trimmedValue === '' ? '' : trimmedValue

  // If unchanged, don't make API call
  const currentValue = membership.projectRole || ''
  if (projectRoleValue === currentValue) {
    delete editingProjectRole.value[membership.id]
    return
  }

  try {
    const { data } = await updateMemberRoleMutation({
      membershipId: membership.id,
      role: membership.role,
      projectRole: projectRoleValue, // Send "" to clear, or the string value
    })

    if (data?.updateMemberRole?.errors?.length) {
      throw new Error(data.updateMemberRole.errors.join(', '))
    }
    delete editingProjectRole.value[membership.id]

    // Emit success alert BEFORE refetch
    emit('show-alert', {
      type: 'success',
      title: s__('ProjectSettings|Success'),
      message: s__('ProjectSettings|Project role updated successfully'),
    })

    await refetch()
  } catch (e) {
    console.error('[MembersList] Error in updateProjectRole', e)
    // Don't delete from editingProjectRole on error so user can try again

    // Emit error alert BEFORE refetch
    emit('show-alert', {
      type: 'error',
      title: s__('ProjectSettings|Error'),
      message: sprintf(s__('ProjectSettings|Failed to update project role: %{error}'), {
        error: e.message || s__('ProjectSettings|Unknown error'),
      }),
    })

    await refetch() // Revert UI
  }
}

async function removeMember(membership) {
  const userName = membership.user.name || membership.user.username || `User ${membership.user.id}`

  // Check if there are unfinished tasks assigned to this user
  if (membership.unfinishedTasksCount > 0) {
    emit('remove-member-with-tasks', membership)
    return
  }

  // No unfinished tasks, proceed with removal
  const confirmed = await showConfirm(`Remove ${userName} from the project?`)
  if (!confirmed) return

  await executeMemberRemoval(membership.id, null)
}

async function executeMemberRemoval(membershipId, reassignToUserIdValue) {
  try {
    const { data } = await removeMemberMutation({
      membershipId: membershipId,
      reassignToUserId: reassignToUserIdValue || null,
    })
    if (data?.removeMember?.errors?.length) {
      throw new Error(data.removeMember.errors.join(', '))
    }

    const reassignedCount = data?.removeMember?.reassignedTasksCount || 0
    const userStillInGroup = data?.removeMember?.userStillInGroup || false

    let message = s__('ProjectSettings|Member removed.')
    if (reassignedCount > 0) {
      message +=
        ' ' +
        sprintf(s__('ProjectSettings|%{count} task(s) were reassigned.'), {
          count: reassignedCount,
        })
    }
    if (userStillInGroup) {
      message +=
        '\n\n' +
        s__(
          'ProjectSettings|⚠️ Warning: This user is still in the Telegram group and can easily rejoin the project.'
        )
    }

    alert(message)

    await refetch()
  } catch (e) {
    alert('Failed to remove member: ' + (e.message || 'Unknown error'))
  }
}

async function refetch() {
  if (props.onRefetch) {
    await props.onRefetch()
  } else {
    emit('refetch')
  }
}
</script>

<style src="../../styles/forms.css"></style>
<style scoped>
.member-item {
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.member-item:last-child {
  border-bottom: none;
}

.role-select {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.role-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.role-owner {
  background: #f0f0f0;
  color: #333;
}

.role-admin {
  background: #e3f2fd;
  color: #1976d2;
}

.role-member {
  background: #f5f5f5;
  color: #666;
}

.btn-sm {
  display: flex;
  align-items: center;
  gap: 4px;
}

.member-remove-btn {
  padding-left: 10px;
  padding-right: 10px;
}

.project-role-save-btn {
  height: 40px;
  min-height: 40px;
  padding: 0 12px;
}

.project-role-save-btn :deep(.plus-icon) {
  color: #2aabee !important;
}

.btn-text {
  display: inline-block;
}

.btn-mobile-icon {
  display: none;
  align-items: center;
}

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .project-role-save-btn {
    width: 40px;
    min-width: 40px;
    max-width: 40px;
    padding: 0;
    justify-content: center;
  }

  .project-role-save-btn .btn-text {
    display: none;
  }

  .project-role-save-btn .btn-mobile-icon {
    display: inline-flex;
  }
}

.info-block {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0;
  margin-bottom: 16px;
}

.info-block-header {
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  transition: background-color 0.2s;
}

.info-block-header:hover {
  background-color: #e9ecef;
}

.info-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.info-title {
  flex: 1;
  font-weight: 500;
  color: #495057;
}

.info-toggle-icon {
  color: #6c757d;
  font-size: 12px;
  flex-shrink: 0;
}

.info-block-content {
  padding: 12px 14px;
  border-top: 1px solid #dee2e6;
  background: white;
}

.role-info-item {
  margin-bottom: 12px;
}

.role-info-item:last-child {
  margin-bottom: 0;
}

.role-info-item strong {
  display: block;
  color: #212529;
  margin-bottom: 4px;
  font-size: 14px;
}

.role-info-item p {
  margin: 0;
  color: #6c757d;
  font-size: 13px;
  line-height: 1.5;
}
</style>
